/** Response contract for `GET dashboard/config` — the single post-login call
 *  that bootstraps the insight UI. Mirrors the backend's
 *  `UserDashboardConfigResponse` (modules/services/Dashboard/types.ts). */

export interface ConfigOption {
  value: string;
  label: string;
}

/** One filter dropdown row from `dashboard/config`'s `filterOptions`: the SP's
 *  own columns plus `value` (what the metric API takes) and `label` (what the
 *  dropdown shows). A row carrying `ServiceLineSystemID` belongs to that
 *  service line only — the same branch (e.g. the corporate "COR") can appear
 *  under more than one. */
export interface FilterOption extends ConfigOption {
  ServiceLineSystemID?: string;
  [column: string]: unknown;
}

/** A branch as the Compare / Charlie pickers use it. */
export interface BranchOption extends ConfigOption {
  branchCode?: string;
}

/** A role the user may view the dashboard as — drives the "Viewing as" picker. */
export interface UserRoleOption {
  id: number | string;
  name: string;
}

/**
 * The RENDER SPEC for one dashboard, as `dashboard/config` returns it in
 * `dashboards[]` — how the Operating View should draw the tab: its filter bar,
 * its detail table, its drill views, its extra cards.
 *
 * Every field is optional and every field is TOLERANT: this is server data, so
 * the resolver (`lib/utils/daylight-insight/dashboardSpec.ts`) drops what it
 * doesn't recognise rather than trusting it. A row carrying only
 * `dashboardId` + `label` still renders — it just falls back to the defaults.
 *
 * One row per (role × dashboard): the row IS the grant as well as the spec —
 * a role's tabs are exactly the rows whose `roleId` matches, in server order.
 */
export interface DashboardDefinition {
  dashboardId: number | string;
  /** The role this row grants the dashboard to. */
  roleId: number | string;
  /** Stable slug the UI routes on. Optional for the dashboards that shipped
   *  with this build (their key is derived from the label); REQUIRED in
   *  practice for a new one, so its tab has an identity of its own. */
  key?: string | null;
  /** Tab label and page title. */
  label?: string | null;
  description?: string | null;
  /** Which page shell renders it: the filtered metrics page (default) or the
   *  unfiltered as-of-now snapshot (Current State). */
  page?: "metrics" | "snapshot" | null;
  /** The "Drill by …" chip, e.g. "Drill by Patient · Episode". Should match the
   *  same dashboard's `DrillGrain` in `[DayLight].[GetMetricDefinitions]`. */
  drillGrain?: string | null;
  /** Filter bar, in display order. A bare string is sugar for `{ key }`; the
   *  object form carries a display `name`, Branch's `source`, and `isPrimary`
   *  (primary = dropdown, false = "+ Focus" dimension; missing = primary). An empty
   *  array means "no filters". */
  filters?: (string | { key: string; name?: string; source?: string; isPrimary?: boolean })[] | null;
  /** The detail tables under the KPIs, rendered in array order. A bare string
   *  or a single object is accepted as a one-table list; `[]` means none. */
  table?: string | DashboardTableDefinition | DashboardTableDefinition[] | null;
  /** Service-line slugs ("hh" | "hos") this tab appears under. Missing, null
   *  or empty = every service line. */
  serviceLines?: string[] | null;
  /** Extra cards between the KPI row and the detail table. */
  sections?: { type: string; metricCode?: string }[] | null;
}

/** One detail table in a dashboard's `table[]`. */
export interface DashboardTableDefinition {
  /** Stable id within the dashboard. Defaults to `kind`. */
  key?: string;
  kind: string;
  /** Heading on the table card and its export sheet. */
  title?: string;
  /** Client-side sliders over the table's own columns. */
  rowFilters?: {
    column: string;
    label?: string;
    domain?: [number, number];
    unit?: "percent" | "count";
  }[];
}

export interface DashboardConfigResponse {
  customerSystemID: string;
  serviceLines: ConfigOption[];
  /** Every filter's dropdown options, keyed by config node name (`branch`,
   *  `payor`, `productivityBranch`, …). A filter reads the node its `key` (and
   *  `source`) names — see `filterOptionNode`. */
  filterOptions: Record<string, FilterOption[]>;
  roles: UserRoleOption[];
  /** Render specs, one per (role × dashboard) — this IS the access model: the
   *  tabs a role sees are the rows whose `roleId` matches. */
  dashboards: DashboardDefinition[];
  /** Last warehouse refresh, verbatim from the SP (e.g. "07/16/2026 05:53 AM"). */
  dataRefreshDate: string | null;
}

// ─── Metric definitions (GET dashboard/metric-definitions) ────────────────────

/** One metric this metric is related to, as the SP authored it. */
export interface MetricDefinitionRelation {
  relationId: number | string | null;
  metricCode: string;
  metricName: string;
  /** How the two relate, e.g. "DRIVES". Empty when the SP omits it. */
  relationship: string;
}

/** A metric's dictionary entry. Everything past `code`/`name` is nullable —
 *  the SP only returns the fields an admin has actually authored, so the guide
 *  renders a row only when its text is there. Note what is NOT in this contract:
 *  benchmark, target and threshold. The SP returns no such columns. */
export interface MetricDefinition {
  code: string;
  name: string;
  shortDesc: string | null;
  longDesc: string | null;
  whyItMatters: string | null;
  formula: string | null;
  /** "Count" | "Percent" | "Average" | "Sum" | "AverageDIP" — how the value is
   *  computed. Drives the unit chip. */
  calc: string | null;
  drillGrain: string | null;
  sequence: number;
  relations: MetricDefinitionRelation[];
}

/** One dashboard (= metric category) and the metrics defined under it. */
export interface MetricDefinitionDashboard {
  dashboardId: number | string | null;
  name: string;
  description: string | null;
  drillGrain: string | null;
  metrics: MetricDefinition[];
}

export interface MetricDefinitionsResponse {
  customerSystemID: string;
  serviceLineSystemID: string;
  serviceLineName: string | null;
  dashboards: MetricDefinitionDashboard[];
}

// ─── Dashboard metrics (POST dashboard/dashboard-metric) ──────────────────────
// Mirrors the backend's DashboardMetricsResponse
// (modules/services/Dashboard/types.ts). Fired when a dashboard/category is
// opened; today only the By-Branch table consumes it.

export interface MetricTrendPoint {
  startDate: string;
  endDate: string;
  value: number | null;
  summaryCode: string;
}

export interface MetricBranchValue {
  branchSystemID: string;
  branchCode: string;
  branchName: string;
  value: number | null;
  priorValue: number | null;
}

export interface DashboardMetric {
  code: string;
  name: string;
  category: string;
  serviceLineSystemID: string;
  formula: string;
  benchmarkLabel: string | null;
  /** Aggregation/unit hint from the SP (Count | Average | Percent | …). */
  calc: string;
  currentValue: number | null;
  priorValue: number | null;
  higherIsBetter: boolean | null;
  isKeyMetric: boolean;
  isCurrentState: boolean;
  sequence: number;
  trend: MetricTrendPoint[];
  branches: MetricBranchValue[];
}

export interface DashboardMetricCategory {
  category: string;
  metrics: DashboardMetric[];
}

export interface DashboardMetricsResponse {
  customerSystemID: string;
  serviceLineSystemID: string;
  dashboardID: number | string;
  startDate: string;
  endDate: string;
  categories: DashboardMetricCategory[];
}

// ─── Raw SP shape ─────────────────────────────────────────────────────────────
// The backend may return the SP's raw `FOR JSON` document as-is (three result
// sets) instead of the formatted `categories`. The UI tolerates both — see
// `toMetricCategories` in metricsNormalize.ts.

export interface RawDashboardMetricRow {
  MetricCategory?: string;
  MetricCode?: string;
  MetricShortDesc?: string;
  MetricFormula?: string;
  BenchMarkLabel?: string | null;
  MetricCalc?: string;
  Curr_MetricValue?: number | null;
  Prev_MetricValue?: number | null;
  TargetHigherIsBetter?: number | boolean | null;
  IsKeyMetric?: number | boolean | null;
  IsCurrentState?: number | boolean | null;
  MetricSequence?: number | null;
}

export interface RawMetricTrendRow {
  MetricCode?: string;
  StartOfPeriod?: string;
  EndofPeriod?: string;
  MetricValue?: number | null;
  SummaryCode?: string;
}

export interface RawMetricBranchRow {
  BranchSystemID?: string;
  branch_code?: string;
  branch_name?: string;
  MetricCode?: string;
  MetricValue?: number | null;
  PrevMetricValue?: number | null;
}

export interface RawDashboardMetricsPayload {
  DashBoardMetrics?: RawDashboardMetricRow[];
  MetricTrend?: RawMetricTrendRow[];
  MetricBranch?: RawMetricBranchRow[];
}

/** Whatever the endpoint returns — formatted (`categories`) or the SP's raw
 *  three result sets. `toMetricCategories` normalizes either into categories. */
export type DashboardMetricsData =
  | DashboardMetricsResponse
  | RawDashboardMetricsPayload;

// ─── Metric relations (POST dashboard/metric-relations) ───────────────────────

/** One related-metric edge for the Trends-View Relationship section. */
export interface MetricRelation {
  MetricCode: string;
  RelatedMetricCode: string;
}

/** One point of a metric's trend series, as returned by the relations SP. */
export interface MetricTrendValue {
  CustomerSystemID?: string;
  SummaryTimePeriod?: string;
  SummaryPeriodStartDate?: string;
  MetricCategoryID?: number;
  MetricCode?: string;
  MetricValue?: number | null;
  /** Trailing 90-day average for this point — drives the trend table's "vs 90D"
   *  check/cross (value above the average → ✓). */
  MetricValue90DayAvg?: number | null;
  /** Present only on the branch-trend response — the branch this point belongs
   *  to (compare-view Branches mode). */
  BranchCode?: string;
}

/** One RELATED metric's card-level figures, as the relations document reports
 *  them: the same numbers that metric's own dashboard card would show, measured
 *  under the drill's date range and filters.
 *
 *  This is what makes a CROSS-DASHBOARD related metric renderable. The dashboard
 *  metrics call only ever covers the open dashboard, so a related metric from
 *  another one (Operations → Admissions relates to Sales & Funnel's ReferralCNT)
 *  has no card anywhere on the page to read a value off.
 *
 *  NOTE the asymmetry, which the drill is built around: the SP returns these for
 *  the metrics the REQUESTED one relates to, never for the requested metric
 *  itself. Asking for AdmissionCNT returns ReferralCNT's and ReferralConv's
 *  details; asking for ReferralCNT returns ITS relations' details and nothing
 *  about ReferralCNT. So a drill into a related metric has to carry the row its
 *  parent's response already brought back — see `MetricTrendsPanel`. */
export interface RelatedMetricDetail {
  CustomerSystemID?: string;
  /** The dashboard the metric belongs to, e.g. "Sales & Funnel". */
  MetricCategory?: string;
  MetricCode?: string;
  StartDate?: string;
  EndDate?: string;
  Curr_MetricValue?: number | string | null;
  Prev_MetricValue?: number | string | null;
  /** Display name — what that metric's dashboard card is titled. */
  MetricShortDesc?: string;
  MetricFormula?: string;
  BenchMarkLabel?: string | null;
  /** Count | Percent | Average | … — the unit the value formats in. */
  MetricCalc?: string;
  TargetHigherIsBetter?: number | boolean | null;
  IsCurrentState?: number | boolean | null;
  IsKeyMetric?: number | boolean | null;
}

/** The relations endpoint returns the SP's document unchanged — the selected
 *  metric's trend series, its related-metric edges, and the card-level figures
 *  of the metrics on the far end of those edges. */
export interface MetricRelationsResponse {
  MetricTrendValues?: MetricTrendValue[];
  MetricRelations?: MetricRelation[];
  RelatedMetricDetails?: RelatedMetricDetail[];
}

/** Request body for POST dashboard/metric-relations — the date context plus the
 *  selected metric's code(s), with every filter in `filters` (the SP's JSON
 *  `@Filters`). */
export interface MetricRelationsParams {
  startDate: string;
  endDate: string;
  serviceLineSystemID: string;
  metricCodes: string;
  /** The applied selection keyed by config filter `key`. Null when nothing is
   *  selected. */
  filters?: Record<string, string[]> | null;
}

// ─── Metric summaries (drill-down tabs) ───────────────────────────────────────

/** Request body for POST dashboard/metric-summary — the relations shape plus
 *  the dimension to group by and the SP's optional @SummarizeByInterval flag
 *  (omitted → NULL). */
export interface MetricSummaryParams extends MetricRelationsParams {
  /** The dimension to group by (`@SummarizeBy`), see `summarizeByFor`. */
  summarizeBy?: string;
  summarizeByInterval?: number;
}

/** Request body for POST dashboard/metric-discipline-notifications — the date
 *  context, the card's metric code, every filter in `filters` (the SP's JSON
 *  `@Filters`), and the SP's @SummarizeByInterval flag. */
export interface DisciplineNotificationParams {
  startDate: string;
  endDate: string;
  serviceLineSystemID: string;
  /** The by-Discipline card's metric code, e.g. "ETA_PercentEligibleTextsSent". */
  metricCodes: string;
  /** The applied selection keyed by config filter `key`. Null when nothing is
   *  selected. */
  filters?: Record<string, string[]> | null;
  /** 1 = the interval the range implies, 0 = the next interval down. */
  summarizeByInterval?: number;
}

/** A normalized summary row (branch or payor), regardless of the SP's columns. */
export interface MetricSummaryRow {
  id: string;
  name: string;
  value: number | null;
  /** Trailing 90-day average for this row (`MetricValue90DayAvg`) — drives the
   *  summary table's "VS 90D" check/cross, the same rule the Trend table uses.
   *  Null when the SP omits it. */
  avg90: number | null;
}

/** Request body for POST dashboard/clinician-details — the By-Clinician ETA
 *  table. The date context plus every filter in `filters` (the SP's JSON
 *  `@Filters`). */
export interface ClinicianDetailsParams {
  startDate: string;
  endDate: string;
  serviceLineSystemID: string;
  /** The applied selection keyed by config filter `key`. Null when nothing is
   *  selected. */
  filters?: Record<string, string[]> | null;
}

/** One row from `GetFactsETATextsByClinician`. The SP aliases columns with
 *  spaces, so keys are quoted; values arrive as strings or numbers. Optional
 *  throughout — the client reads defensively. */
export interface ClinicianDetailRow {
  /** Columns are DYNAMIC — the SP aliases whatever the dashboard needs, so the
   *  table derives its headers from the keys. The named ones below are the ETA
   *  set the Patient Notifications dashboard returns. */
  [column: string]: unknown;
  Clinician?: string;
  Discipline?: string;
  Branch?: string;
  "% Eligible ETA Text Sent"?: number | string | null;
  "Texts Sent"?: number | string | null;
  "Visits Opted-In to Texting"?: number | string | null;
  "Visits Completed"?: number | string | null;
  "Worker Email"?: string | null;
  "First Name"?: string | null;
  "Last Name"?: string | null;
}

/** Request body for POST dashboard/branch-details — the By-Branch table. The
 *  date context, every filter in `filters` (the SP's JSON `@Filters`), plus the
 *  dashboard id. */
export interface BranchDetailsParams {
  startDate: string;
  endDate: string;
  serviceLineSystemID: string;
  /** The applied selection keyed by config filter `key`. Null when nothing is
   *  selected. */
  filters?: Record<string, string[]> | null;
  /** Nullable: the dashboard By-Branch table sends the pillar's dashboard id;
   *  the All Metrics By-Branch view sends null (spans every dashboard). */
  dashboardID?: number | string | null;
}

/** One row from `GetMetricsValuesForBranchDashboard` — one (branch × metric)
 *  value. `MetricValue`/`PrevMetricValue` may be absent on a branch that has no
 *  data for that metric. */
export interface BranchDetailRow {
  CustomerSystemID?: string;
  BranchSystemID?: string;
  branch_code?: string;
  branch_name?: string;
  MetricCode?: string;
  /** Metric display name from the SP — used as the column header. */
  MetricShortDesc?: string;
  MetricValue?: number | string | null;
  PrevMetricValue?: number | string | null;
}

/** Request body for POST /all-metric/interval — the All Metrics Day/Week/Month
 *  grid. `dashboardID` null = all pillars; the rest mirror the metrics filters. */
export interface AllMetricIntervalParams {
  /** Currently selected date (the interval window's end). */
  intervalEndDate: string;
  /** DAY | WEEK | MONTH. */
  interval: string;
  serviceLineSystemID: string;
  branchSystemIDs?: string | null;
  payorTypeSystemIDs?: string | null;
  disciplines?: string | null;
  repRosterSystemIDs?: string | null;
  referralSubTypes?: string | null;
  /** Referral Source filter — the selected source NAMES (`@ReferralSourceNames`). */
  referralSourceNames?: string | null;
  /** Job Title filter — the selected titles (`@JobTitles`). */
  jobTitles?: string | null;
  dashboardID?: number | string | null;
  numberOfIntervals?: number;
}

/** One row of the All Metrics interval grid — a (metric × interval) data point
 *  from `GetMetricValuesForDashboardInterval`. */
export interface AllMetricIntervalRow {
  MetricCategory?: string;
  MetricCode?: string;
  MetricShortDesc?: string;
  MetricCalc?: string;
  Curr_MetricValue?: number | string | null;
  Prev_MetricValue?: number | string | null;
  TargetHigherIsBetter?: boolean | number | null;
  isKeyMetric?: boolean | number;
  isCurrentState?: boolean | number;
  Interval?: string;
  IntervalStartDate?: string;
  /** 1 → this metric has underlying records: the row's hover affordance reads
   *  "Records" and opens the drill-down modal (`GetMetricDrillDown`). 0/absent →
   *  "Details", which opens the metric's trends drill instead. */
  HasDrillDown?: boolean | number | null;
}

// ─── Metric drill-down (POST all-metric/drill-down) ───────────────────────────

/** Request body for POST all-metric/drill-down — the records behind one metric
 *  in the All Metrics grid, scoped to the interval clicked and the filters
 *  currently applied on that screen. */
export interface MetricDrillDownParams {
  /** The metric to list records for (`@MetricCode`). */
  metricCode: string;
  /** The clicked interval's window. */
  startDate: string;
  endDate: string;
  serviceLineSystemID: string;
  branchSystemIDs?: string | null;
  payorTypeSystemIDs?: string | null;
}

/** One drill-down record. Columns are DYNAMIC — they differ per metric — so a row
 *  is just a bag of cells and the table derives its headers from the keys. */
export type MetricDrillDownRow = Record<string, unknown>;

/** One row of `POST /dashboard/metric-discipline-notifications`. The SP emits a
 *  row per (discipline × interval × MetricCode) and always returns all four ETA
 *  notification metrics, so a single date/discipline pair spans four rows —
 *  `MetricCode` is what tells them apart. `Discipline` may carry trailing spaces
 *  ("HHA "). */
export interface DisciplineNotificationRow {
  CustomerSystemID?: string;
  SummaryInterval?: string;
  DateSummaryStart?: string;
  DateSummaryEnd?: string;
  MetricCode?: string;
  Discipline?: string;
  MetricValue?: number | null;
  MetricValue90DayAvg?: number | null;
}

/** Request body for POST dashboard/dashboard-metric. */
export interface DashboardMetricsParams {
  startDate: string;
  endDate: string;
  serviceLineSystemID: string;
  dashboardID: number | string;
  /** Applied filters keyed by the config filter `key`, each the selected
   *  options' values — the SP's `@Filters` JSON. Null when nothing is
   *  selected. */
  filters: Record<string, string[]> | null;
  includePriorPeriod?: boolean;
  /** `@SaveUserFilterPreferences` — whether the SP saves this filter selection
   *  as the user's preference (server default: true). False while the filters
   *  come from a drill Focus. Not part of the query key: it changes what the SP
   *  writes, not what it returns. */
  saveUserFilterPreferences?: boolean;
}

/** Column layout stored in `@PerfDetails` for a saved custom view. */
export type CustomViewType =
  | "branch"
  | "clinician"
  | "month"
  | "week"
  | "day";

export interface CustomViewDetails {
  /** Which table / pivot this view belongs to. */
  viewType: CustomViewType;
  /** Visible column keys (metric codes, period ISO dates, clinician keys, …). */
  columns: string[];
  /** Active sort, if any. */
  sort: { col: string; dir: "asc" | "desc" } | null;
  /** Whether this is the view to select automatically on the next load. Stored
   *  inside `PerfDetails`, so exactly one saved view per table should carry
   *  `true`; a newly saved view is always `false`. */
  isDefault: boolean;
}

/** Request body for POST dashboard/save-custom-view. */
export interface SaveCustomViewParams {
  perfName: string;
  dashboardID?: number | string | null;
  perfDetails: CustomViewDetails;
}

/** Request body for POST dashboard/custom-views. */
export interface GetCustomViewsParams {
  dashboardID?: number | string | null;
}

/** The `PerfDetails` document of a `PerfCode = 'UserFilter'` row: the selected
 *  option values per config filter key — the same `@Filters` map the metrics SP
 *  is sent, and the shape it now stores (e.g. `{ "payor": ["SKYRA-01-15"] }`).
 *
 *  `SaveAppUserPerfs` replaces `PerfDetails` wholesale, so a key left out is a
 *  filter that is not set; `{}` is "every filter cleared".
 *
 *  NO DATES. The metrics SP does not store a date range in this row, so neither
 *  does this — writing one would make the range newly sticky per dashboard. */
export type UserFilterDetails = Record<string, string[]>;

/** Request body for POST dashboard/save-user-filter.
 *
 *  `dashboardID` is REQUIRED — a filter selection is always scoped to one
 *  dashboard, so there is no `null` ("All Metrics") case the way there is for a
 *  custom view. */
export interface SaveUserFilterParams {
  dashboardID: number | string;
  perfDetails: UserFilterDetails;
}

/** One `[DayLight].[GetAppUserPerfs]` row. Column names vary with the SP's
 *  aliases, so everything is optional and read defensively; `PerfDetails` is the
 *  JSON string the save call stored. */
export interface CustomViewRow {
  AppUserPerfID?: number | string | null;
  PerfName?: string | null;
  PerfCode?: string | null;
  PerfDetails?: string | Record<string, unknown> | null;
  DashboardID?: number | string | null;
  [column: string]: unknown;
}

/** A saved view once its `PerfDetails` JSON has been parsed. */
export interface SavedCustomView {
  /** The row's `AppUserPerfID` — what the delete call takes. Null when the SP
   *  didn't return one, in which case the view can't be deleted server-side. */
  id: number | string | null;
  name: string;
  details: CustomViewDetails;
}

/** Request body for POST dashboard/delete-custom-view. */
export interface DeleteCustomViewParams {
  appUserPerfID: number | string;
}

/** The `PerfCode` of the row that carries a dashboard's saved FILTER selections,
 *  as opposed to the `CustomView` rows that carry its table column layouts. Both
 *  come back from the same `dashboard/custom-views` call. */
export const USER_FILTER_PERF_CODE = "UserFilter";

/**
 * The filter selections saved against a dashboard (`PerfCode = 'UserFilter'`),
 * once its `PerfDetails` JSON has been parsed.
 *
 * `values` is keyed by config filter `key` (`branch`, `payor`, …) and holds the
 * option VALUES the metric APIs take, so applying a saved filter is a lookup
 * back to the label the control renders. A key with no entry means "all".
 */
export interface SavedDashboardFilters {
  values: Record<string, string[]>;
  /** Saved date range, if one was stored. Both ends must be present for the
   *  range to be applied. */
  startDate: string | null;
  endDate: string | null;
}
